import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useAsyncValidatePhone } from 'hooks/useAsyncValidations';

const useValidatePhoneNumber = () => {
  const intl = useIntl();
  const { mutateAsync: validatePhone } = useAsyncValidatePhone();
  const cache = useMemo(() => new Map(), []);
  const pendingRequests = useRef(new Map());

  return useCallback(
    async (value) => {
      if (value.length !== 10) {
        return false;
      }

      if (cache.has(value)) {
        return cache.get(value);
      }

      // If there's already a pending request for this value, return its promise
      if (pendingRequests.current.has(value)) {
        return pendingRequests.current.get(value);
      }

      // Create new promise and store it
      const promise = validatePhone(value).then(({ isValid }) => {
        pendingRequests.current.delete(value);

        if (isValid) {
          cache.set(value, true);
          return true;
        }

        const errorMessage = intl.formatMessage({
          id: 'afforda.formHouseholdProfile.phoneNumberInvalid',
        });
        cache.set(value, errorMessage);
        return errorMessage;
      });

      pendingRequests.current.set(value, promise);
      return promise;
    },
    [cache, intl, validatePhone],
  );
};

export default useValidatePhoneNumber;
